// ---------------------------------------------------------------------------
// The overview's lists, and what it shows when one cannot be loaded (R15a)
//
// The overview (dashboard.ts) loads two lists, the campaigns and the projects.
// It used to count only a thrown error as a failure: an answer { ok: false }
// (a 500, an expired session) left the campaigns empty, so the four figures
// said 0, and any failure of the projects left the grid empty, without a word.
// Zero campaigns and "nothing could be read" are different facts, and the page
// has to say which one it has.
//
// loadList turns every answer that is not the list into a failure with its
// reason, and never throws. loadOverview reads both lists and hands each answer
// to the page's state. overviewFailure says which list failed, and
// failureCallout is what the Overview shows (R15b): the system's Callout,
// critical (role alert), the title the fact, the reason its text, Try again
// its one small action, right under the page's header, where ConsoleOverview
// puts what needs a person. loadFailure is R15a's card with the same three
// parts, which the campaign page (R16a) still shows in its legacy island
// until it is rebuilt: unchanged, so that page does not change here.
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';

import { button } from '../design/button.js';
import { callout } from '../design/callout.js';
import { uncountedCampaigns } from './dashboard-figures.js';

/** A list the server sent, or why there is none. An empty list is a list. */
export type Loaded<T> = { ok: true; items: T[] } | { ok: false; error: string };

/** The reason when the request never reached the server. */
export const NO_CONNECTION = 'The server could not be reached. Check your connection.';

/**
 * Reads `GET url`, which answers `{ ok: true, [key]: [...] }`. Every other
 * answer is a failure: `ok: false` (with the server's reason), an HTTP error,
 * a body that is not JSON, a body without the list, or no answer at all.
 */
export async function loadList<T>(
  url: string,
  key: string,
  get: (url: string) => Promise<Response> = u => fetch(u),
): Promise<Loaded<T>> {
  let resp: Response;
  try {
    resp = await get(url);
  } catch {
    return { ok: false, error: NO_CONNECTION };
  }
  const data = (await resp.json().catch(() => null)) as Record<string, unknown> | null;
  const list = data?.[key];
  if (resp.ok && data?.ok === true && Array.isArray(list)) return { ok: true, items: list as T[] };
  const reason = typeof data?.error === 'string' ? data.error.trim() : '';
  if (reason) return { ok: false, error: reason };
  if (!resp.ok) return { ok: false, error: `The server answered ${resp.status}.` };
  return { ok: false, error: `The server's answer held no ${key}.` };
}

/** The page's own setters, which loadOverview hands each answer to. */
export interface OverviewSetters<C, P> {
  setCampaigns: (campaigns: C[]) => void;
  setCampaignsError: (error: string | null) => void;
  setProjects: (projects: P[]) => void;
  setProjectsError: (error: string | null) => void;
}

/**
 * Reads both of the overview's lists at once and hands each answer to the
 * page. A list that loaded replaces the page's list and clears its failure. A
 * list that did not load leaves the page's list as it was and records why:
 * an empty list never stands in for a failure. Never throws.
 *
 * This is the page's wiring, here rather than in dashboard.ts so that it is
 * tested by behaviour (R15a review: `setCampaignsError(null)` in the page
 * brought the four zeros back without a failing test).
 *
 * `warn` hears about every campaign that came without its live Sent count
 * (R15c): the page shows "—" for it, and this says which, once per load.
 */
export async function loadOverview<C extends { _id: string }, P>(
  set: OverviewSetters<C, P>,
  get: (url: string) => Promise<Response> = u => fetch(u),
  warn: (message: string) => void = message => console.warn(message),
): Promise<void> {
  const [campaigns, projects] = await Promise.all([
    loadList<{ _id?: unknown } | null>('/api/campaigns', 'campaigns', get),
    loadList<P>('/api/projects', 'projects', get),
  ]);
  if (campaigns.ok) {
    // Every campaign gets a string _id, and one without is dropped: rows and
    // their links are keyed by it.
    const listed = campaigns.items
      .map(c => ({ ...c, _id: typeof c?._id === 'string' ? c._id : String(c?._id ?? '') }) as unknown as C)
      .filter(c => c._id.length > 0);
    set.setCampaigns(listed);
    // The Sent column and the Leads contacted tile read the live count the
    // list route attaches to every campaign (campaign-live-counts.ts). One
    // without it means that route stopped attaching it: say so where it shows.
    const uncounted = uncountedCampaigns(listed);
    if (uncounted.length > 0) {
      warn(
        `[dashboard] ${uncounted.length} campaign(s) came without a live Sent count, shown as "—": ${uncounted.join(', ')}`,
      );
    }
  }
  set.setCampaignsError(campaigns.ok ? null : campaigns.error);
  if (projects.ok) set.setProjects(projects.items);
  set.setProjectsError(projects.ok ? null : projects.error);
}

/**
 * What stands where the grid (or a project's campaigns) would be, or null when
 * nothing failed that the view needs. Without the campaigns every count on the
 * page would be a guess, at both levels. Without the projects only the grid is
 * missing; inside a project its campaigns loaded and are shown. When both
 * failed, each reason is kept.
 */
export function overviewFailure(state: {
  campaignsError: string | null;
  projectsError: string | null;
  inProject: boolean;
}): { title: string; reason: string } | null {
  const { campaignsError, projectsError, inProject } = state;
  if (campaignsError && projectsError) {
    return {
      title: 'Your campaigns and projects could not be loaded',
      reason:
        campaignsError === projectsError ? campaignsError : `Campaigns: ${campaignsError} Projects: ${projectsError}`,
    };
  }
  if (campaignsError) return { title: 'Your campaigns could not be loaded', reason: campaignsError };
  if (projectsError && !inProject) return { title: 'Your projects could not be loaded', reason: projectsError };
  return null;
}

/** What could not be loaded, and why, with Try again: the old card, for the campaign page until R16 rebuilds it. */
export function loadFailure(title: string, reason: string, onRetry: () => void): TemplateResult {
  return html`<div class="card bg-base-100" role="alert">
    <div class="card-body items-center gap-4 py-16 text-center">
      <div>
        <h2 class="text-lg font-medium">${title}</h2>
        <p class="text-base-content/60 mt-1">${reason}</p>
      </div>
      <button type="button" class="btn" @click=${onRetry}>Try again</button>
    </div>
  </div>`;
}

/** What could not be loaded, and why, with Try again, as the system's critical Callout (the Overview, R15b). */
export function failureCallout(title: string, reason: string, onRetry: () => void): TemplateResult {
  return callout(
    { tone: 'critical', title, actions: button({ size: 'sm', onClick: () => onRetry() }, 'Try again') },
    reason,
  );
}
